import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { usePortalStore } from './store/portalStore';
import { getPortalUrl } from './lib/portal';
import { mixpanel } from './lib/mixpanel';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';

import { EventDetailPage } from './pages/EventDetailPage';
import { EventsPage } from './pages/EventsPage';
import { InsightsPage } from './pages/InsightsPage';
import { AdminPage } from './pages/AdminPage';
import { WaitlistOnboardingPage } from './pages/WaitlistOnboardingPage';
import { WaitlistPendingPage } from './pages/WaitlistPendingPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ViewerHomePage } from './pages/ViewerHomePage';
import { BecomeACreatorPage } from './pages/BecomeACreatorPage';
import { AlbumViewPage } from './pages/AlbumViewPage';
import { AlbumPreviewPage } from './pages/AlbumPreviewPage';
import { SlideshowScreenPage } from './pages/SlideshowScreenPage';
import { PublicGalleryPage } from './pages/PublicGalleryPage';
import { WrongPortalPage } from './pages/WrongPortalPage';
import { TalkPage } from './pages/TalkPage';
import { ReactNexusTalkPage } from './pages/ReactNexusTalkPage';
import { LegalPage } from './pages/LegalPage';
import { ManagerInvitePrompt } from './components/Notifications/ManagerInvitePrompt';
import { PermissionDeniedModal } from './components/UI/PermissionDeniedModal';
import { OfflineBanner } from './components/UI/OfflineBanner';
import { FullPageSpinner } from './components/UI/Spinner';
import { UploadPill } from './components/Gallery/UploadPill';
import { DownloadPanel } from './components/Download/DownloadPanel';
import { ViewerLayout } from './layouts/ViewerLayout';
import { CreatorLayout } from './layouts/CreatorLayout';
import { pageVariants } from './constants/animations';
import { registerUploadSW } from './services/uploadManager';
import appStyles from './App.module.css';

const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    variants={pageVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
  >
    {children}
  </motion.div>
);

const OwnerGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  if (!user?.isOwner) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const WaitlistLayout: React.FC = () => {
  const { user, initialized } = useAuthStore();
  const portal = usePortalStore((s) => s.portal);

  if (!initialized) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.betaApproved || user.isOwner) {
    return <Navigate to={portal === 'creator' ? '/events' : '/'} replace />;
  }

  return <Outlet />;
};

const RootRedirect: React.FC = () => {
  const { user } = useAuthStore();
  const portal = usePortalStore((s) => s.portal);
  if (user) return <Navigate to={portal === 'creator' ? '/events' : '/albums'} replace />;
  return <PageTransition><LandingPage /></PageTransition>;
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const portal = usePortalStore((s) => s.portal);

  useEffect(() => {
    mixpanel.track('Page View', { path: location.pathname });
  }, [location.pathname]);

  if (portal === 'creator') {
    return (
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
        <Route path="/talk" element={<TalkPage />} />
        <Route path="/reactnexustalk" element={<ReactNexusTalkPage />} />
        <Route path="/legal/:type" element={<LegalPage />} />
        <Route element={<WaitlistLayout />}>
          <Route path="/waitlist/onboarding" element={<WaitlistOnboardingPage />} />
        </Route>
        <Route element={<CreatorLayout />}>
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/events/:id/gallery" element={<Navigate to=".." replace />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/admin" element={<OwnerGuard><AdminPage /></OwnerGuard>} />
        </Route>
        <Route path="/slideshow/:albumId/:slideshowToken" element={<SlideshowScreenPage />} />
        {/* Short, typeable display link for entering at a TV. */}
        <Route path="/tv/:code" element={<SlideshowScreenPage />} />
        <Route path="/tv" element={<SlideshowScreenPage />} />
        <Route path="/albums" element={<WrongPortalPage targetPortal="viewer" />} />
        <Route path="/albums/:albumId" element={<WrongPortalPage targetPortal="viewer" />} />
        <Route path="/p/:token" element={<PageTransition><PublicGalleryPage /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
      <Route path="/talk" element={<TalkPage />} />
      <Route path="/reactnexustalk" element={<ReactNexusTalkPage />} />
      <Route path="/legal/:type" element={<LegalPage />} />
      <Route path="/join/:token" element={<AlbumPreviewPage />} />
      <Route path="/slideshow/:albumId/:slideshowToken" element={<SlideshowScreenPage />} />
      {/* Short, typeable display link for entering at a TV. */}
      <Route path="/tv/:code" element={<SlideshowScreenPage />} />
      <Route path="/tv" element={<SlideshowScreenPage />} />
      <Route path="/p/:token" element={<PageTransition><PublicGalleryPage /></PageTransition>} />
      <Route element={<WaitlistLayout />}>
        <Route path="/waitlist/pending" element={<WaitlistPendingPage />} />
        <Route path="/waitlist/onboarding" element={<WaitlistOnboardingPage />} />
      </Route>
      <Route path="/become-a-creator" element={<PageTransition><BecomeACreatorPage /></PageTransition>} />
      <Route path="/admin" element={<OwnerGuard><AdminPage /></OwnerGuard>} />
      <Route element={<ViewerLayout />}>
        <Route path="/albums" element={<ViewerHomePage />} />
        <Route path="/albums/:albumId" element={<AlbumViewPage />} />
      </Route>
      <Route path="/events" element={<WrongPortalPage targetPortal="creator" />} />
      <Route path="/events/:id" element={<WrongPortalPage targetPortal="creator" />} />
      <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
    </Routes>
  );
};

const App: React.FC = () => {
  const { fetchMe, initialized, user } = useAuthStore();
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const portal = usePortalStore((s) => s.portal);

  useEffect(() => {
    fetchMe();
    registerUploadSW();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mixpanel.register({ portal });
  }, [portal]);


  // Wrong-portal guard: block non-approved users from the creator portal
  useEffect(() => {
    if (!initialized || !user) return;

    const onCreator = portal === 'creator';
    const isCreatorUser = user.betaApproved || user.isOwner;

    if (onCreator && !isCreatorUser) {
      // Non-approved user on creator portal → send to viewer waitlist
      if (import.meta.env.PROD) {
        window.location.href = getPortalUrl('viewer') + '/waitlist/pending';
      } else {
        console.warn('[portal] Non-approved user on creator portal — in prod would redirect to viewer');
      }
    }
  }, [initialized, user, portal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!initialized) return <FullPageSpinner />;

  const toasterMount = document.getElementById('toaster-root');

  return (
    <BrowserRouter>
      <OfflineBanner />
      <AnimatedRoutes />
      {user && <ManagerInvitePrompt />}
      <PermissionDeniedModal />
      <UploadPill />
      {/* Mounted once at the root so a client-side ZIP download keeps running
          while the user navigates around the app. */}
      <DownloadPanel />
      {toasterMount && createPortal(
        <Toaster
          position="top-right"
          // Sit above every modal/sheet (editor = 10200, sheets = 10201) so
          // toasts/nudges are never hidden behind an open editor modal.
          containerStyle={{ zIndex: 10300 }}
          toastOptions={{
            style: resolvedTheme === 'dark'
              ? { background: '#242237', color: '#f2f0ff', border: '1px solid rgba(255,255,255,0.07)' }
              : { background: '#ffffff', color: '#1c1a2e', border: '1px solid rgba(0,0,0,0.07)' },
          }}
        />,
        toasterMount,
      )}
    </BrowserRouter>
  );
};

export default App;
